/* eslint-disable react-hooks/exhaustive-deps */
import {
  useMediaQuery,
  Grid,
  Box,
  useTheme,
  Typography,
  Button,
  Container,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import IconCheckCotizador from "@components/icons/IconCheckCotizador";
import {
  useLazyGetAgriculturalCostsByProductIdQuery,
  useLazyGetAgriculturalCycleTypeConfigByProductIdQuery,
  useLazyGetHydrologicalRegimeTypeConfigByProductIdQuery,
  useLazyGetPropertyLocationConfigByProductIdQuery,
  useLazyGetSowingTypeConfigByProductIdQuery,
} from "@api/catalogs";
import {
  ProductAgriculturalCycleTypeStructure,
  ProductCreditConfigurationStructure,
  ProductHydrologicalRegimeTypeStructure,
  ProductPropertyLocationStructure,
  ProductSowingTypeEstructure,
} from "@interfaces/catalogs";
import { formatCurrency } from "@helpers/funciones";
import { calculateMaturityCollection, calculateMinistrationSchedule } from "@pages/producto/credito_simple/sfi/helpers";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { TaxSystemConfigResponse } from "@interfaces/index";

const PRODUCT_ID = "26043131-38bd-4b78-94a6-ce4b6f320580";
const TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PM = "SFI01";
const TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PFAE = "SFI02";

type CropKey = "maiz" | "sorgo" | "trigo";
type CicloKey = "OI" | "PV";
type PersonTypeKey = "PM" | "PFAE";

const CROP_LABELS: Record<CropKey, string> = {
  maiz: "Maíz",
  sorgo: "Sorgo",
  trigo: "Trigo",
};

const normalizeCropLabel = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const getCropKeyFromSowingTypeName = (value: string): CropKey | null => {
  const normalizedValue = normalizeCropLabel(value);

  if (normalizedValue === "maiz") {
    return "maiz";
  }

  if (normalizedValue === "sorgo") {
    return "sorgo";
  }

  if (normalizedValue === "trigo") {
    return "trigo";
  }

  return null;
};

const getCicloKeyFromAgriculturalCycleTypeName = (value: string): CicloKey | null => {
  const normalizedValue = normalizeCropLabel(value);

  if (normalizedValue === "oi" || normalizedValue.includes("otono")) {
    return "OI";
  }

  if (normalizedValue === "pv" || normalizedValue.includes("primavera")) {
    return "PV";
  }

  return null;
};

const CICLO_FULL_LABEL: Record<CicloKey, string> = {
  OI: "OI 2025/2026",
  PV: "PV 2026",
};

const PERSON_TYPE_OPTIONS: { value: PersonTypeKey; title: string; subtitle: string }[] = [
  { value: "PM", title: "Persona Moral", subtitle: "Empresa / sociedad" },
  { value: "PFAE", title: "Persona física", subtitle: "Actividad empresarial" },
];

const formatDueDate = (iso: string) => {
  const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const [year, month, day] = iso.split("T")[0].split("-").map(Number);

  return `${day} ${MONTHS[month - 1]} ${year}`;
};

const MAX_SURFACE = 5000;

const getSafeSurface = (value: string) => {
  const numeric = Number(value);

  if (Number.isNaN(numeric) || numeric <= 0) {
    return 0;
  }

  return Math.min(numeric, MAX_SURFACE);
};

type ChipOption = {
  value: string;
  title: string;
  subtitle?: string;
  isSelected: boolean;
  onClick: () => void;
};

type CropOption = {
  value: CropKey;
  title: string;
  config: ProductSowingTypeEstructure;
};

type PropertyLocationOption = {
  value: string;
  title: string;
  config: ProductPropertyLocationStructure;
};

type AgriculturalCycleTypeOption = {
  value: CicloKey;
  title: string;
  config: ProductAgriculturalCycleTypeStructure;
};

type HydrologicalRegimeTypeOption = {
  value: string;
  title: string;
  config: ProductHydrologicalRegimeTypeStructure;
};

const buildHydrologicalRegimeTypeOption = (
  item: ProductHydrologicalRegimeTypeStructure,
): HydrologicalRegimeTypeOption => ({
  value: item.hydrological_regime_type_id,
  title: item.hydrological_regime_type.description || item.hydrological_regime_type.name,
  config: item,
});

const CotizadorSfiAvicola = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [getSowingTypeConfigByProductId] = useLazyGetSowingTypeConfigByProductIdQuery();
  const [getPropertyLocationConfigByProductId] = useLazyGetPropertyLocationConfigByProductIdQuery();
  const [getAgriculturalCycleTypeConfigByProductId] = useLazyGetAgriculturalCycleTypeConfigByProductIdQuery();
  const [getHydrologicalRegimeTypeConfigByProductId] = useLazyGetHydrologicalRegimeTypeConfigByProductIdQuery();
  const [getAgriculturalCostsByProductId] = useLazyGetAgriculturalCostsByProductIdQuery();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();

  const [selectedCrop, setSelectedCrop] = useState<CropOption | null>(null);
  const [selectedPropertyLocation, setSelectedPropertyLocation] = useState<PropertyLocationOption | null>(null);
  const [selectedCiclo, setSelectedCiclo] = useState<AgriculturalCycleTypeOption | null>(null);
  const [selectedTec, setSelectedTec] = useState<HydrologicalRegimeTypeOption | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<PersonTypeKey | null>(null);
  const [surface, setSurface] = useState<string>("50");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sowingType, setSowingType] = useState<ProductSowingTypeEstructure[]>([]);
  const [propertyLocation, setPropertyLocation] = useState<ProductPropertyLocationStructure[]>([]);
  const [agriculturalCycleType, setAgriculturalCycleType] = useState<ProductAgriculturalCycleTypeStructure[]>([]);
  const [hydrologicalRegimeType, setHydrologicalRegimeType] = useState<ProductHydrologicalRegimeTypeStructure[]>([]);
  const [agriculturalCosts, setAgriculturalCosts] = useState<ProductCreditConfigurationStructure | null>(null);
  const [configPM, setConfigPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAE, setConfigPFAE] = useState<TaxSystemConfigResponse | null>(null);
  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const safeSurface = getSafeSurface(surface);

  const cropOptions = sowingType
    .filter((item) => item.active)
    .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
    .map((item) => {
      const cropKey = getCropKeyFromSowingTypeName(item.sowing_type.name);

      if (!cropKey) {
        return null;
      }

      return {
        value: cropKey,
        title: item.sowing_type.description || item.sowing_type.name,
        config: item,
      };
    })
    .filter((item): item is CropOption => item !== null)
    .filter((item, index, array) => array.findIndex((currentItem) => currentItem.value === item.value) === index);

  const propertyLocationOptions = propertyLocation
    .filter((item) => item.active)
    .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
    .map((item) => ({
      value: item.property_location.name,
      title: item.property_location.description || item.property_location.name,
      config: item,
    }))
    .filter((item, index, array) => array.findIndex((currentItem) => currentItem.value === item.value) === index);

  const agriculturalCycleTypeOptions = agriculturalCycleType
    .filter((item) => item.active)
    .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
    .map((item) => {
      const cicloKey = getCicloKeyFromAgriculturalCycleTypeName(item.agricultural_cycle_type.name);

      if (!cicloKey) {
        return null;
      }

      return {
        value: cicloKey,
        title: item.agricultural_cycle_type.description || item.agricultural_cycle_type.name,
        config: item,
      };
    })
    .filter((item): item is AgriculturalCycleTypeOption => item !== null)
    .filter((item, index, array) => array.findIndex((currentItem) => currentItem.value === item.value) === index);

  const hydrologicalRegimeTypeOptions = hydrologicalRegimeType
    .filter((item) => item.active)
    .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
    .map(buildHydrologicalRegimeTypeOption)
    .filter((item, index, array) => array.findIndex((currentItem) => currentItem.value === item.value) === index);

  const selectedCropKey = selectedCrop?.value ?? null;
  const selectedEstado = selectedPropertyLocation?.value ?? null;
  const selectedCicloKey = selectedCiclo?.value ?? null;

  const showZona = !!selectedCrop;
  const showCiclo = showZona && !!selectedEstado;
  const showRegimen = showCiclo && !!selectedCiclo;
  const showPersona = showRegimen && !!selectedTec;
  const showSuperficie = showPersona && !!selectedPersona;

  const showResult = !!agriculturalCosts && showSuperficie && safeSurface > 0;

  const totalProjectCost = agriculturalCosts ? agriculturalCosts.credit_payment_per_hectare * safeSurface : 0;
  const requestedCredit = agriculturalCosts ? agriculturalCosts.credit_quota_per_hectare * safeSurface : 0;
  const producerContribution = totalProjectCost - requestedCredit;
  const maturityCollection = agriculturalCosts
    ? calculateMaturityCollection(requestedCredit, agriculturalCosts.maturity_date).total
    : 0;
  const ministrationList = calculateMinistrationSchedule(agriculturalCosts?.ministrations, safeSurface);

  const cropLower = selectedCropKey ? CROP_LABELS[selectedCropKey].toLowerCase() : "el cultivo";

  const handleCropChange = async (crop: CropOption) => {
    try {
      const propertyLocation = await getPropertyLocationConfigByProductId({
        product_id: PRODUCT_ID,
        sowing_type_id: crop.config.sowing_type_id,
      }).unwrap();
      setSelectedCrop(crop);
      setSelectedPropertyLocation(null);
      setSelectedCiclo(null);
      setSelectedTec(null);
      setSelectedPersona(null);
      setPropertyLocation(propertyLocation);
    } catch (error) {
      console.error("Error handling crop change:", error);
    }
  };

  const handleEstadoChange = async (estado: string) => {
    try {
      if (!selectedCropKey) {
        return;
      }

      const nextPropertyLocation = propertyLocationOptions.find((item) => item.value === estado) || null;

      if (!nextPropertyLocation) {
        return;
      }

      const agriculturalCycleType = await getAgriculturalCycleTypeConfigByProductId({
        product_id: PRODUCT_ID,
        property_location_id: nextPropertyLocation.config.property_location_id,
      }).unwrap();

      setAgriculturalCycleType(agriculturalCycleType);

      setSelectedPropertyLocation(nextPropertyLocation);
      setSelectedCiclo(null);
      setSelectedTec(null);
    } catch (error) {
      console.error("Error handling crop change:", error);
    }
  };

  const handleCicloChange = async (ciclo: AgriculturalCycleTypeOption) => {
    try {
      if (!selectedCropKey || !selectedEstado) {
        return;
      }

      const hydrologicalRegimeType = await getHydrologicalRegimeTypeConfigByProductId({
        product_id: PRODUCT_ID,
        agricultural_cycle_type_id: ciclo.config.agricultural_cycle_type_id,
      }).unwrap();

      setHydrologicalRegimeType(hydrologicalRegimeType);
      setSelectedCiclo(ciclo);

      const activeRegimeTypes = hydrologicalRegimeType.filter((item) => item.active);

      setSelectedTec(activeRegimeTypes.length === 1 ? buildHydrologicalRegimeTypeOption(activeRegimeTypes[0]) : null);
    } catch (error) {
      console.error("Error handling ciclo change:", error);
    }
  };

  const handleSurfaceChange = (value: string) => {
    if (value !== "" && !/^\d*\.?\d?$/.test(value)) {
      return;
    }

    if (value !== "" && Number(value) > MAX_SURFACE) {
      return;
    }

    setSurface(value);
  };

  const handleSurfaceBlur = () => {
    const numericValue = Number(surface);

    if (surface === "" || Number.isNaN(numericValue) || numericValue <= 0) {
      setSurface("1");
    }
  };

  useEffect(() => {
    if (selectedPersona === "PM" && configPM) {
      setCotizadorConfig(configPM);
    } else if (selectedPersona === "PFAE" && configPFAE) {
      setCotizadorConfig(configPFAE);
    }
  }, [selectedPersona, configPM, configPFAE]);

  const handleContinue = () => {
    if (!agriculturalCosts || !selectedPersona || !safeSurface) {
      return;
    }

    const sourceCode = sessionStorage.getItem("source_code");
    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${requestedCredit.toFixed(2)}&loan_term=24&tax_system_type=${cotizadorConfig.tax_system_type}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }

    navigate(urlPortal);
  };

  const sharedButtonProps = {
    isMovile: isMobileOrTablet,
    icon: <IconCheckCotizador backgroundColor={theme?.palette?.primary.accent} width={22} height={22} />,
    width_icon: "22px",
    height_icon: "22px",
    top_icon: "-12px",
    font_size: "14px",
    font_size_movil: "12px",
    font_weight: 600,
  };

  const buttonGridItemSx = {
    display: "flex",
    justifyContent: "center",
    "& > *": {
      width: isMobileOrTablet ? "100%" : "auto",
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getSowingTypeConfigByProductId({
          product_id: PRODUCT_ID,
        }).unwrap();
        const responseTaxPm = await getTaxSystemConfig(TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PM).unwrap();
        const responseTaxPfae = await getTaxSystemConfig(TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PFAE).unwrap();
        setConfigPM(responseTaxPm);
        setConfigPFAE(responseTaxPfae);
        setSowingType(response);
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedCrop || !selectedPropertyLocation || !selectedCiclo || !selectedTec) {
      setAgriculturalCosts(null);
      return;
    }

    const fetchAgriculturalCosts = async () => {
      try {
        const response = await getAgriculturalCostsByProductId({
          product_id: PRODUCT_ID,
          sowing_type_id: selectedCrop.config.sowing_type_id,
          property_location_id: selectedPropertyLocation.config.property_location_id,
          agricultural_cycle_type_id: selectedCiclo.config.agricultural_cycle_type_id,
          hydrological_regime_type_id: selectedTec.config.hydrological_regime_type_id,
        }).unwrap();

        setAgriculturalCosts(response);
      } catch (error) {
        console.error("Error fetching agricultural costs:", error);
        setAgriculturalCosts(null);
      }
    };

    fetchAgriculturalCosts();
  }, [selectedCrop, selectedPropertyLocation, selectedCiclo, selectedTec]);

  const renderStepHeader = (number: number, title: string) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: "primary.dark",
          color: "common.white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "14px",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography variant="body1" fontWeight={600} color="primary.dark">
        {title}
      </Typography>
    </Box>
  );

  const renderSingleValueBox = (label: string, subtitle: string, bgcolor: string) => (
    <Box px={2.5} py={2.25} border="1px solid #D7E8DE" borderRadius="18px" bgcolor={bgcolor} ml={4.5}>
      <Typography variant="body1" fontWeight={700} color="primary.dark" component="span">
        {label}
      </Typography>
      <Typography variant="body2" color="primary.dark" component="span" sx={{ opacity: 0.65, ml: 1 }}>
        {subtitle}
      </Typography>
    </Box>
  );

  const renderChipOptions = (options: ChipOption[], columnsXs: number) => (
    <Grid container spacing={2} mb={4}>
      {options.map((option) => (
        <Grid item xs={columnsXs} sm={columnsXs} key={option.value} sx={buttonGridItemSx}>
          <ButtonTypeSelectedCustom
            title={
              option.subtitle ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={0.5}>
                  <Typography
                    component="span"
                    color="inherit"
                    fontSize={isMobileOrTablet ? "14px" : "15px"}
                    fontWeight={700}
                    lineHeight={1.2}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    component="span"
                    color="inherit"
                    fontSize={isMobileOrTablet ? "11px" : "12px"}
                    lineHeight={1.2}
                    sx={{ opacity: option.isSelected ? 0.9 : 0.72 }}
                  >
                    {option.subtitle}
                  </Typography>
                </Box>
              ) : (
                option.title
              )
            }
            onClick={option.onClick}
            isSelected={option.isSelected}
            width={columnsXs <= 4 ? "174px" : "264px"}
            widthMoviles="100%"
            height={option.subtitle ? "88px" : "68px"}
            heightMoviles={option.subtitle ? "88px" : "68px"}
            padding={isMobileOrTablet ? "12px 16px" : "12px"}
            {...sharedButtonProps}
          />
        </Grid>
      ))}
    </Grid>
  );

  const selectedZona = propertyLocationOptions.find((item) => item.value === selectedEstado) || null;

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <Container maxWidth={"sm"}>
            <Typography variant="tofu_title" mt={4}>
              Crédito de avío agrícola
            </Typography>

            <Typography variant="body1" color="primary.dark" textAlign="center" mt={1} mb={3}>
              Habilitación y avío para el establecimiento y producción de grano.
            </Typography>

            <Box pb={4}>
              <Box mb={4}>
                {renderStepHeader(1, "¿Qué vas a sembrar?")}

                {isLoading ? (
                  <Typography variant="body2" color="primary.dark" sx={{ opacity: 0.55, pl: 5.5 }}>
                    Cargando tipos de siembra...
                  </Typography>
                ) : cropOptions.length > 0 ? (
                  renderChipOptions(
                    cropOptions.map((crop) => ({
                      value: crop.value,
                      title: crop.title,
                      isSelected: selectedCrop?.config.id === crop.config.id,
                      onClick: () => handleCropChange(crop),
                    })),
                    4,
                  )
                ) : (
                  <Typography variant="body2" color="primary.dark" sx={{ opacity: 0.55, pl: 5.5 }}>
                    No hay tipos de siembra disponibles en este momento.
                  </Typography>
                )}
              </Box>

              {showZona && (
                <Box mb={4}>
                  {renderStepHeader(2, "Ubicación del predio")}

                  <Typography variant="body2" color="primary.dark" mb={1.5} sx={{ opacity: 0.55, pl: 5.5 }}>
                    Solo aparecen los estados con financiamiento disponible para {cropLower}.
                  </Typography>

                  <FormControl fullWidth>
                    <Select
                      value={selectedPropertyLocation?.value ?? ""}
                      onChange={(event: SelectChangeEvent<string>) => handleEstadoChange(event.target.value)}
                      displayEmpty
                      renderValue={(value) => {
                        if (!value || !selectedZona) {
                          return <span style={{ color: "#9AA6A0" }}>Selecciona el estado</span>;
                        }

                        return `${selectedZona.config.property_location.name} — ${selectedZona.title}`;
                      }}
                      sx={{
                        backgroundColor: "common.white",
                        borderRadius: "16px",
                        minHeight: 46,
                        boxShadow: "0px 10px 30px rgba(5, 87, 53, 0.06)",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "#D8E6DF",
                        },
                        ".MuiSelect-select": {
                          py: 1.75,
                          px: 2.25,
                          color: "primary.dark",
                          fontSize: "15px",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 1,
                            borderRadius: "16px",
                            boxShadow: "0px 18px 35px rgba(5, 87, 53, 0.10)",
                          },
                        },
                      }}
                    >
                      {propertyLocationOptions.map((location) => (
                        <MenuItem key={location.config.id} value={location.value}>
                          <Box display="flex" flexDirection="column" gap={0.25} py={0.5}>
                            <Typography variant="body2" color="primary.dark" fontWeight={600}>
                              {location.config.property_location.name}
                            </Typography>
                            <Typography variant="caption" color="primary.dark" sx={{ opacity: 0.6 }}>
                              {location.title}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {showCiclo && (
                <Box mb={4}>
                  {renderStepHeader(3, "Ciclo agrícola")}

                  <FormControl fullWidth>
                    <Select
                      value={selectedCiclo?.value ?? ""}
                      onChange={(event: SelectChangeEvent<string>) => {
                        const nextCiclo =
                          agriculturalCycleTypeOptions.find((item) => item.value === event.target.value) || null;

                        if (nextCiclo) {
                          handleCicloChange(nextCiclo);
                        }
                      }}
                      displayEmpty
                      renderValue={(value) => {
                        if (!value || !selectedCiclo) {
                          return <span style={{ color: "#9AA6A0" }}>Selecciona el ciclo</span>;
                        }

                        return `${selectedCiclo.config.agricultural_cycle_type.name} — ${selectedCiclo.title}`;
                      }}
                      sx={{
                        backgroundColor: "common.white",
                        borderRadius: "16px",
                        minHeight: 46,
                        boxShadow: "0px 10px 30px rgba(5, 87, 53, 0.06)",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "#D8E6DF",
                        },
                        ".MuiSelect-select": {
                          py: 1.75,
                          px: 2.25,
                          color: "primary.dark",
                          fontSize: "15px",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 1,
                            borderRadius: "16px",
                            boxShadow: "0px 18px 35px rgba(5, 87, 53, 0.10)",
                          },
                        },
                      }}
                    >
                      {agriculturalCycleTypeOptions.map((cycle) => (
                        <MenuItem key={cycle.config.id} value={cycle.value}>
                          <Box display="flex" flexDirection="column" gap={0.25} py={0.5}>
                            <Typography variant="body2" color="primary.dark" fontWeight={600}>
                              {cycle.config.agricultural_cycle_type.name}
                            </Typography>
                            <Typography variant="caption" color="primary.dark" sx={{ opacity: 0.6 }}>
                              {cycle.title}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {showRegimen && (
                <Box mb={4}>
                  {renderStepHeader(4, "Régimen hídrico")}

                  {hydrologicalRegimeTypeOptions.length === 1
                    ? renderSingleValueBox(hydrologicalRegimeTypeOptions[0].title, "", "#EEF6F2")
                    : renderChipOptions(
                        hydrologicalRegimeTypeOptions.map((option) => ({
                          value: option.value,
                          title: option.title,
                          isSelected: selectedTec?.value === option.value,
                          onClick: () => setSelectedTec(option),
                        })),
                        6,
                      )}
                </Box>
              )}

              {showPersona && (
                <Box mb={4}>
                  {renderStepHeader(5, "Tipo de persona")}

                  {renderChipOptions(
                    PERSON_TYPE_OPTIONS.map((option) => ({
                      value: option.value,
                      title: option.title,
                      subtitle: option.subtitle,
                      isSelected: selectedPersona === option.value,
                      onClick: () => setSelectedPersona(option.value),
                    })),
                    6,
                  )}
                </Box>
              )}

              {showSuperficie && (
                <Box mt={1} mb={4}>
                  {renderStepHeader(6, "Superficie a sembrar")}

                  <Box display="flex" justifyContent="center" alignItems="center" gap={1.5}>
                    <TextField
                      value={surface}
                      onChange={(event) => handleSurfaceChange(event.target.value)}
                      onBlur={handleSurfaceBlur}
                      inputProps={{
                        inputMode: "decimal",
                        style: { textAlign: "center", fontSize: 27, fontWeight: 600 },
                      }}
                      sx={{
                        width: { xs: 180, sm: 200 },
                        ".MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          backgroundColor: "common.white",
                          minHeight: 58,
                          boxShadow: "0px 4px 12px rgba(5, 86, 42, 0.06)",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E1E7E3",
                        },
                      }}
                    />
                    <Typography fontWeight={500} fontSize={"19px"} color="primary.dark">
                      ha
                    </Typography>
                  </Box>

                  <Typography
                    fontWeight={400}
                    fontSize={"12px"}
                    mt={1.5}
                    color="primary.dark"
                    textAlign={"center"}
                    sx={{ opacity: 0.55 }}
                  >
                    Hasta 5,000 ha · admite 1 decimal
                  </Typography>
                </Box>
              )}

              {showResult && agriculturalCosts && (
                <>
                  <Box
                    mt={2}
                    mb={2.5}
                    p={isMobileOrTablet ? 2.5 : 3}
                    sx={{
                      background: "linear-gradient(180deg, #F4FAF6 0%, #EEF6F1 100%)",
                      border: "1px solid #D7E8DE",
                      borderRadius: "18px",
                      boxShadow: "0px 12px 30px rgba(5, 86, 42, 0.10)",
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color="primary.dark"
                      mb={2}
                      sx={{ textTransform: "uppercase", fontSize: "14px" }}
                    >
                      Monto de crédito
                    </Typography>

                    <Box display="grid" gap={1.5}>
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="body2" color="primary.dark">
                          Costo por ha
                        </Typography>
                        <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                          {formatCurrency(agriculturalCosts.credit_payment_per_hectare)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="body2" color="primary.dark">
                          Cuota de crédito por ha
                        </Typography>
                        <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                          {formatCurrency(agriculturalCosts.credit_quota_per_hectare)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="body2" color="primary.dark">
                          Costo total del proyecto
                        </Typography>
                        <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                          {formatCurrency(totalProjectCost)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="body2" color="primary.dark">
                          Aportación del productor
                        </Typography>
                        <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                          {formatCurrency(producerContribution)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      borderTop="1px solid #D7E8DE"
                      mt={2.5}
                      pt={2.5}
                      display="flex"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Box>
                        <Typography variant="body1" color="primary.dark" fontWeight={700} component="div">
                          Monto de crédito
                        </Typography>
                        <Typography variant="body1" color="primary.dark" fontWeight={700} component="div">
                          a solicitar
                        </Typography>
                      </Box>
                      <Typography component="div" textAlign="right" color="primary.dark" fontWeight={700}>
                        <Box component="span" sx={{ fontSize: isMobileOrTablet ? "28px" : "30px", lineHeight: 1 }}>
                          {formatCurrency(requestedCredit)}
                        </Box>{" "}
                        <Box component="span" sx={{ fontSize: "14px", fontWeight: 500 }}>
                          MXN
                        </Box>
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    mb={2.5}
                    p={isMobileOrTablet ? 2.5 : 3}
                    sx={{
                      border: "1px solid #E3ECE7",
                      borderRadius: "18px",
                      boxShadow: "0px 8px 22px rgba(5, 86, 42, 0.07)",
                    }}
                  >
                    <Typography variant="body1" fontWeight={600} color="primary.dark" mb={0.5}>
                      Calendario de ministraciones y cobro
                    </Typography>
                    <Typography variant="body2" color="primary.dark" mb={2} sx={{ opacity: 0.55, fontSize: "12px" }}>
                      Disposiciones del crédito según las etapas del cultivo
                    </Typography>

                    <Box display="grid" gap={0}>
                      <Box
                        display="grid"
                        gridTemplateColumns="100px 1fr"
                        gap={1.5}
                        pb={1}
                        borderBottom="1px solid #E7EBE8"
                      >
                        <Typography variant="caption" color="primary.dark" fontWeight={600} sx={{ opacity: 0.45 }}>
                          MINISTRACIÓN
                        </Typography>
                        <Typography
                          variant="caption"
                          color="primary.dark"
                          fontWeight={600}
                          textAlign="right"
                          sx={{ opacity: 0.45 }}
                        >
                          MONTO
                        </Typography>
                      </Box>

                      {ministrationList.map((ministration, index) => (
                        <Box
                          key={ministration.order}
                          display="grid"
                          gridTemplateColumns="100px 1fr"
                          gap={1.5}
                          py={1.25}
                          borderBottom="1px solid #F0F3F1"
                        >
                          <Typography variant="body2" color="primary.dark" fontWeight={500}>
                            Ministración {index + 1}
                          </Typography>
                          <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                            {formatCurrency(ministration.credit)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box
                      mt={1.5}
                      px={2}
                      py={1.75}
                      border="1px solid #D7E8DE"
                      borderRadius="12px"
                      bgcolor="#EEF6F1"
                      display="flex"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Box>
                        <Typography variant="body2" color="primary.dark" fontWeight={600}>
                          Cobro al vencimiento
                        </Typography>
                        <Typography variant="caption" color="primary.dark" sx={{ opacity: 0.7 }}>
                          {formatDueDate(agriculturalCosts.maturity_date)} · capital + intereses
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        color="primary.dark"
                        fontWeight={700}
                        fontSize="15.5px"
                        textAlign="right"
                      >
                        {formatCurrency(maturityCollection)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    mb={2.5}
                    p={isMobileOrTablet ? 2 : 2.25}
                    sx={{
                      backgroundColor: "#EEF6F1",
                      border: "1px solid #D7E8DE",
                      borderRadius: "14px",
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                      <Typography variant="body2" color="primary.dark" sx={{ lineHeight: 1.55, fontSize: "12.5px" }}>
                        <Box component="span" fontWeight={700}>
                          Monto estimado, no es una oferta de crédito.
                        </Box>{" "}
                        Cifras de referencia del Sistema AGROCOSTOS de FIRA
                        {selectedCicloKey ? ` (ciclo ${CICLO_FULL_LABEL[selectedCicloKey]})` : ""}. El monto, plazo y
                        calendario definitivos quedan sujetos a aprobación y al dictamen técnico del predio.
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="center" mb={2}>
                    <Button
                      variant="contained"
                      onClick={handleContinue}
                      sx={{ width: "100%", maxWidth: 560, minHeight: 56, borderRadius: "12px" }}
                    >
                      Continuar con mi solicitud
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default CotizadorSfiAvicola;
